import {useMutation, useQuery} from '@tanstack/react-query'
import {useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {AuthService} from '../service/auth.services'
import {StorageService} from '../service/storage.services'
import {UserService} from '../service/user.services'
import useAppStore from '../store'
import {ErrorHelper, isAuthError} from '../types/app.type'
import {ResetPasswordFormType, TLoginResponse} from '../types/auth.type'
import {TUser} from '../types/user.type'
import {useIsMobile} from './responsive.hooks'
import {resolveLandingUrl} from '../utils/resolveLandingUrl'

export const useLogin = (rememberMe: boolean = false) => {
  const navigate = useNavigate()
  const setUser = useAppStore(state => state.setUser)
  const isMobile = useIsMobile()
  const {
    mutate: loginHook,
    error,
    data,
    isSuccess,
    isPending,
  } = useMutation<
    TLoginResponse,
    ErrorHelper,
    {email: string; password: string}
  >({
    mutationFn: loginData =>
      AuthService.login({
        email: loginData.email,
        password: loginData.password,
      }),
    onError: (err: ErrorHelper) => {
      console.error(err)
    },
  })
  useEffect(() => {
    if (data) {
      StorageService.setAuthAccess({
        accessToken: data.authTokens.accessToken,
        refreshToken: data.authTokens.refreshToken,
        expiresIn: data.authTokens.expiresIn,
        refreshTokenExpiresIn: data.authTokens.refreshExpiresIn,
        rememberMe,
        id: data.infos.id,
      })
      setUser(data.infos)
      // When the back resolved a project root, adapt the backend's landing URL:
      // - on mobile, redirect to the activity table
      // - on desktop, redirect to resolveLandingUrl
      const landingUrl = resolveLandingUrl(data.landingUrl, isMobile)
      navigate(landingUrl)
    }
  }, [data])

  return {loginHook, error, isSuccess, isPending}
}

export const useRegister = () => {
  const navigate = useNavigate()
  const {
    mutate: registerHook,
    error,
    data,
    isSuccess,
    isPending,
  } = useMutation<any, ErrorHelper, any>({
    mutationFn: async (registerData: any) => {
      await AuthService.register(registerData)
      navigate('/auth/login')
    },
  })
  return {registerHook, error, data, isSuccess, isPending}
}

export const useForgetPassword = () => {
  const {
    mutate: forgetPasswordHook,
    error,
    data,
    isSuccess,
    isPending,
  } = useMutation<any, ErrorHelper, any>({
    mutationFn: async (email: string) => {
      await AuthService.forgetPassword(email)
    },
  })
  return {forgetPasswordHook, error, data, isSuccess, isPending}
}

export const useResetPassword = () => {
  const {
    mutate: resetPasswordHook,
    error,
    data,
    isSuccess,
    isPending,
  } = useMutation<any, ErrorHelper, any>({
    mutationFn: async (resetData: ResetPasswordFormType) => {
      await AuthService.resetPassword(resetData)
    },
  })
  return {resetPasswordHook, error, data, isSuccess, isPending}
}

/**
 * Restores the authenticated user in Zustand after a page refresh.
 *
 * On a hard reload, the Zustand store resets to its initial state (user = null)
 * while the JWT tokens remain in localStorage. This hook detects that situation
 * and re-fetches the user profile so the rest of the app can treat the session
 * as fully initialised.
 *
 * The fetcher handles token refresh transparently: if the access token has
 * expired, it exchanges the refresh token for new tokens before the query
 * resolves. If both tokens are expired, the fetcher calls AuthService.logout()
 * which redirects to /auth/login.
 */
export const useInitSession = () => {
  const user = useAppStore(state => state.user)
  const setUser = useAppStore(state => state.setUser)
  const userId = StorageService.getUserId()

  const {data, isLoading, isError, error} = useQuery<
    TUser | undefined,
    ErrorHelper
  >({
    queryKey: ['session', 'init', userId],
    queryFn: () => UserService.getUserById(userId),
    // Only run when there is a stored session but no user in memory
    enabled: user === null && userId !== null,
    // Never refetch automatically — token refresh in the fetcher handles validity
    staleTime: Infinity,
    // Fail immediately: the fetcher already retries via the refresh flow
    retry: false,
  })

  // Sync the fetched user into Zustand so the whole app can access it
  useEffect(() => {
    if (data) {
      setUser(data)
    }
  }, [data, setUser])

  // Only force logout when the failure is genuinely an auth problem
  // (invalid/expired/missing token, user deleted). Network failures or 5xx
  // must keep the session intact so the user is not booted out when the API
  // is briefly unreachable (dev server restart, transient connectivity loss).
  useEffect(() => {
    if (isError && isAuthError(error)) {
      AuthService.logout()
    }
  }, [isError, error])

  // isLoadingSession is true only during the brief re-hydration window
  const isLoadingSession = isLoading && user === null && userId !== null

  return {
    isLoadingSession,
    isSessionUnreachable: isError && !isAuthError(error),
  }
}
